// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import {
  collection,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  collectionGroup,
  addDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

import { OBSERVATIONS_COLLECTION } from './FirebaseObservationUtils';

const ACTIVE_GLOBAL_LABELSETS_DOC = 'labelsets/global_labelsets';
const GLOBAL_LABELSETS_COLLECTION = 'labelsets/global_labelsets/global_labelsets';
const LABELSET_PROFILES_COLLECTION = 'labelsets/labelset_profiles/labelset_profiles';
const LABELING_TASKS_COLLECTION = (clientId) => `clients/${clientId}/labeling_tasks`;
const LABELING_TASKS_COLLECTION_GROUP = 'labeling_tasks';

export const LABELING_TASK_STATUSES = ['in_progress', 'stopped', 'completed', 'archived'];

/**
 * Get the ids of the active global labelsets
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The active global labelset ids
 */
export async function getActiveGlobalLabelsetData(firestore) {
  const activeGlobalLabelsetsRef = doc(firestore, ACTIVE_GLOBAL_LABELSETS_DOC);
  const activeGlobalLabelsetsSnap = await getDoc(activeGlobalLabelsetsRef);
  return activeGlobalLabelsetsSnap.data();
}

/**
 * Get the active global labelsets
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The active global labelsets
 */
export async function getActiveGlobalLabelsets(firestore) {
  const activeGlobalLabelsetData = await getActiveGlobalLabelsetData(firestore);
  const ids = Object.values(activeGlobalLabelsetData?.active_labelsets || {});
  if (ids.length === 0) return {};

  const labelsetResults = await Promise.all(ids.map((labelsetId) => getGlobalLabelsetById(firestore, labelsetId)));

  const activeGlobalLabelsets = ids.reduce((acc, labelsetId, index) => {
    const labelset_id = labelsetResults[index]?.labelset;
    acc[labelset_id] = labelsetResults[index];
    return acc;
  }, {});

  return activeGlobalLabelsets;
}

/**
 * Construct labelset groups from the labelsets and active global labelset data
 * Will fetch the labelsets and active global labelset data from the database if not provided
 * @param {Firestore} firestore - The firestore instance
 * @param {Object} labelsets - The labelsets data
 * @param {Object} activeGlobalLabelsetData - The active global labelset data (containing metadata about labelset groups)
 * @returns {Object} - The labelset groups
 */
export async function constructLabelsetGroups(firestore, labelsets, activeGlobalLabelsetData = null) {
  // If no activeGlobalLabelsetData provided, fetch it directly from the database
  if (!activeGlobalLabelsetData) {
    activeGlobalLabelsetData = await getActiveGlobalLabelsetData(firestore);
  }

  // Construct labelset groups
  const labelsetGroups = {};
  Object.entries(activeGlobalLabelsetData?.groups || {}).forEach(([groupId, displayName]) => {
    labelsetGroups[groupId] = {
      labelset: groupId,
      display_name: displayName,
      labels: {},
    };
  });

  Object.values(labelsets).forEach((labelset) => {
    if (!labelset.group) return;
    if (!labelsetGroups[labelset.group]) return;
    labelsetGroups[labelset.group].type = labelset.type;
    labelsetGroups[labelset.group].labels = {
      ...labelsetGroups[labelset.group].labels,
      ...labelset.labels,
    };
  });

  return labelsetGroups;
}

/**
 * Get a specific global labelset by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} labelsetId - The ID of the labelset to retrieve
 * @returns {Object|null} - The labelset data or null if not found
 */
export async function getGlobalLabelsetById(firestore, labelsetId) {
  const labelsetRef = doc(firestore, GLOBAL_LABELSETS_COLLECTION, labelsetId);
  const labelsetSnap = await getDoc(labelsetRef);

  if (labelsetSnap.exists()) {
    return {
      id: labelsetSnap.id,
      ...labelsetSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Get all labelset profiles
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The labelset profiles data
 */
export async function getLabelsetProfiles(firestore) {
  const labelsetProfiles = {};
  const labelsetProfilesRef = collection(firestore, LABELSET_PROFILES_COLLECTION);
  const labelsetProfilesSnap = await getDocs(labelsetProfilesRef);
  labelsetProfilesSnap.forEach((doc) => {
    labelsetProfiles[doc.id] = doc.data();
  });
  return labelsetProfiles;
}

/**
 * Get a specific labelset profile by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} labelsetProfileId - The id of the labelset profile to retrieve
 * @returns {Object|null} - The labelset profile data or null if not found
 */
export async function getLabelsetProfileById(firestore, labelsetProfileId) {
  const labelsetProfileRef = doc(firestore, LABELSET_PROFILES_COLLECTION, labelsetProfileId);
  const labelsetProfileSnap = await getDoc(labelsetProfileRef);

  if (labelsetProfileSnap.exists()) {
    return {
      id: labelsetProfileSnap.id,
      ...labelsetProfileSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Get a labelset profile by its role for the client. The default role is "default".
 * @param {Firestore} firestore - The firestore instance
 * @param {Object} clientData - The client data
 * @param {string} role - The role of the labelset profile for the client. The default role is "default".
 * @returns {Object|null} - The labelset profile data or null if not found
 */
export async function getLabelsetProfileByClientRole(firestore, clientData, role = 'default') {
  const labelsetProfileId = (clientData?.labelset_profiles || {})?.[role];
  if (!labelsetProfileId) {
    console.error(`No labelset profile found for role ${role}`);
    return null;
  }

  return getLabelsetProfileById(firestore, labelsetProfileId);
}

/**
 * Get the display name of a labelset
 * @param {Object} labelset - The labelset
 * @param {string} languageCode - The language code
 * @returns {string} - The display name of the labelset
 */
export function getLabelsetDisplayName(labelset, languageCode = 'en') {
  const labelsetDisplayNames = labelset?.display_name || {};
  if (languageCode in labelsetDisplayNames) {
    return labelsetDisplayNames[languageCode];
  } else if ('en' in labelsetDisplayNames) {
    return labelsetDisplayNames['en'];
  } else {
    return labelset;
  }
}

/**
 * Get the display name of a label
 * @param {Object} labelset - The labelset
 * @param {string} label - The label
 * @param {string} languageCode - The language code
 * @returns {string} - The display name of the label
 */
export function getLabelDisplayName(labelset, label, languageCode = 'en') {
  const labelDisplayNames = labelset?.labels?.[label]?.display_name || {};
  if (languageCode in labelDisplayNames) {
    return labelDisplayNames[languageCode];
  } else if ('en' in labelDisplayNames) {
    return labelDisplayNames['en'];
  } else {
    return label;
  }
}

/**
 * Get all labelsets IDs belonging to a specific group
 * @param {Object} labelsets - The labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelsets for
 * @returns {string[]} - The ids of the labelsets belonging to the group
 */
export function getAllLabelsetsBelongingToGroup(labelsets, groupId) {
  if (!labelsets) return [];
  return Object.keys(labelsets).filter((labelsetId) => labelsets[labelsetId]?.group === groupId);
}

/**
 * Get a labelset belonging to a specific group
 * @param {Object} labelsetIds - The ids of the labelsets to search through
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelset for
 * @returns {string|null} - The labelset-id belonging to the group or null if not found. If multiple labelsets belong to the group, the first one is returned.
 */
export function getLabelsetBelongingToGroup(labelsetIds, globalLabelsets, groupId) {
  if (!labelsetIds || typeof labelsetIds !== 'object') return null;

  for (const labelsetId of labelsetIds) {
    if (!(labelsetId in globalLabelsets)) continue;

    if (globalLabelsets[labelsetId]?.group === groupId) {
      return labelsetId;
    }
  }
  return null;
}

/**
 * From all labelsets belonging to a specific group, get the labelset that contains a specific label
 * @param {string} labelId - The id of the label to search for
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelset for
 * @returns {string|null} - The labelset-id containing the label or null if not found. If multiple labelsets contain the label, the first one is returned.
 */
export function getLabelsetBelongingToGroupByLabel(labelId, globalLabelsets, groupId) {
  for (const [labelsetId, labelset] of Object.entries(globalLabelsets)) {
    // Check if this labelset belongs to the group
    if (labelset?.group !== groupId) continue;

    // Check if this labelset contains the label
    if (labelId in (labelset?.labels || {})) {
      return labelsetId;
    }
  }
  return null;
}

/**
 * From a dictionary of labelsetId -> labelId, get the labelId that matches the labelset belonging to a specific group
 * @param {Object} labelsetIdToLabelId - The dictionary of labelsetId -> labelId
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the label for
 * @returns {string|null} - The labelId that matches the labelset belonging to the group or null if not found. If multiple labelIds match the labelset, the first one is returned.
 */
export function getLabelIdMatchingGroup(labelsetIdToLabelId, globalLabelsets, groupId) {
  const labelsetId = getLabelsetBelongingToGroup(Object.keys(labelsetIdToLabelId || {}), globalLabelsets, groupId);
  if (!labelsetId || !(labelsetId in labelsetIdToLabelId)) return null;
  const labelId = labelsetIdToLabelId[labelsetId];
  return labelId;
}

/**
 * From a dictionary of labelsetId -> labelId, get the display name of the label that matches the labelset belonging to a specific group
 * @param {Object} labelsetIdToLabelId - The dictionary of labelsetId -> labelId
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the label for
 * @param {string} languageCode - The language code
 * @returns {string|null} - The display name of the label that matches the labelset belonging to the group or null if not found.
 */
export function getLabelDisplayNameMatchingGroup(labelsetIdToLabelId, globalLabelsets, groupId, languageCode = 'en') {
  const labelsetId = getLabelsetBelongingToGroup(Object.keys(labelsetIdToLabelId || {}), globalLabelsets, groupId);
  if (!labelsetId || !(labelsetId in labelsetIdToLabelId)) return null;
  let labelId = labelsetIdToLabelId[labelsetId];

  // If a whole object is provided, extract the label or class
  if (typeof labelId === 'object') {
    if (labelId.label) {
      labelId = labelId.label;
    } else if (labelId.class) {
      labelId = labelId.class;
    } else {
      return null;
    }
  }
  return getLabelDisplayName(globalLabelsets[labelsetId], labelId, languageCode);
}

/**
 * Get all labeling tasks across all clients
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksAcrossAllClients(firestore) {
  const labelingTasks = {};
  const labelingTasksRef = collectionGroup(firestore, LABELING_TASKS_COLLECTION_GROUP);
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to labeling tasks across all clients
 * @param {Firestore} firestore - The firestore instance
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export async function subscribeToLabelingTasksAcrossAllClients(firestore, callback) {
  const labelingTasksRef = collectionGroup(firestore, LABELING_TASKS_COLLECTION_GROUP);
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Get a specific labeling task by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task to retrieve
 * @returns {Object|null} - The labeling task data or null if not found
 */
export async function getLabelingTaskById(firestore, clientId, labelingTaskId) {
  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  const labelingTaskSnap = await getDoc(labelingTaskRef);
  if (labelingTaskSnap.exists()) {
    return {
      id: labelingTaskSnap.id,
      ...labelingTaskSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Subscribe to a specific labeling task by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task to subscribe to
 * @param {Function} callback - The callback function to call when the labeling task changes
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTaskById(firestore, clientId, labelingTaskId, callback) {
  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  const unsubscribe = onSnapshot(labelingTaskRef, (doc) => {
    if (doc.exists()) {
      callback({
        id: doc.id,
        ...doc.data(),
      });
    } else {
      callback(null);
    }
  });
  return unsubscribe;
}

/**
 * Get all labeling tasks for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksForClient(firestore, clientId) {
  const labelingTasks = {};
  const labelingTasksRef = collection(firestore, LABELING_TASKS_COLLECTION(clientId));
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to all labeling tasks for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTasksForClient(firestore, clientId, callback) {
  const labelingTasksRef = collection(firestore, LABELING_TASKS_COLLECTION(clientId));
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Get all labeling tasks for a user
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} userId - The id of the user
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksForUser(firestore, clientId, userId) {
  const labelingTasks = {};
  const labelingTasksRef = query(
    collection(firestore, LABELING_TASKS_COLLECTION(clientId)),
    where('assigned_user_ids', 'array-contains', userId),
  );
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to all labeling tasks for a user
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} userId - The id of the user
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTasksForUser(firestore, clientId, userId, callback) {
  const labelingTasksRef = query(
    collection(firestore, LABELING_TASKS_COLLECTION(clientId)),
    where('assigned_user_ids', 'array-contains', userId),
  );
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Create a new labeling task for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelsetProfileId - The id of the labelset profile
 * @param {string} taskName - The display name for the task
 * @param {string} description - The description of the task
 * @param {string[]} assignedUserIds - The ids of the users assigned to the task
 * @param {string[]} observationIds - The ids of the observations to label
 * @param {string} createdBy - The id of the user who created the task
 * @param {string} collaborationType - The collaboration type of the task (collaborative or individual)
 * @param {string} status - The status of the task (in_progress, stopped, completed, archived)
 */
export async function createLabelingTask(
  firestore,
  clientId,
  labelsetProfileId,
  taskName,
  description = null,
  assignedUserIds = [],
  observationIds = [],
  createdBy = null,
  collaborationType = 'collaborative',
  status = 'in_progress',
) {
  // assert collaboration type is either "collaborative" or "individual"
  if (collaborationType !== 'collaborative' && collaborationType !== 'individual') {
    throw new Error("Collaboration type must be either 'collaborative' or 'individual'");
  }

  // Validate status
  if (!LABELING_TASK_STATUSES.includes(status)) {
    throw new Error('Status must be one of the following: ' + LABELING_TASK_STATUSES.join(', '));
  }

  const labelingTaskData = {
    labelset_profile_id: labelsetProfileId,
    client_id: clientId,
    task_name: taskName,
    description: description,
    assigned_user_ids: assignedUserIds,
    observation_ids: observationIds,
    created_by: createdBy,
    created_at: serverTimestamp(),
    collaboration_type: collaborationType,
    status: status,
  };

  await addDoc(collection(firestore, LABELING_TASKS_COLLECTION(clientId)), labelingTaskData);
}

/**
 * Edit a labeling task. Only the fields that are provided will be updated.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {Object} options - The update options
 * @param {string} [options.taskName] - The display name for the task
 * @param {string} [options.description] - The description of the task
 * @param {string} [options.collaborationType] - The collaboration type of the task (collaborative or individual)
 * @param {string[]} [options.assignedUserIds] - The ids of the users assigned to the task
 * @param {string[]} [options.observationIds] - The ids of the observations to label
 * @param {string} [options.status] - The status of the task (in_progress, stopped, completed, archived)
 * @param {string} [options.labelsetProfileId] - The id of the labelset profile
 */
export async function editLabelingTask(firestore, clientId, labelingTaskId, options = {}) {
  const { taskName, description, collaborationType, assignedUserIds, observationIds, status, labelsetProfileId } =
    options;

  // Validate collaboration type if provided
  if (collaborationType !== undefined && collaborationType !== 'collaborative' && collaborationType !== 'individual') {
    throw new Error("Collaboration type must be either 'collaborative' or 'individual'");
  }

  // Validate status if provided
  if (status !== undefined && !LABELING_TASK_STATUSES.includes(status)) {
    throw new Error('Status must be one of the following: ' + LABELING_TASK_STATUSES.join(', '));
  }

  // Build update object with only provided fields
  const updateData = {};
  if (taskName !== undefined) updateData.task_name = taskName;
  if (description !== undefined) updateData.description = description;
  if (collaborationType !== undefined) updateData.collaboration_type = collaborationType;
  if (assignedUserIds !== undefined) updateData.assigned_user_ids = assignedUserIds;
  if (observationIds !== undefined) updateData.observation_ids = observationIds;
  if (status !== undefined) updateData.status = status;
  if (labelsetProfileId !== undefined) updateData.labelset_profile_id = labelsetProfileId;

  // Only update if there are fields to update
  if (Object.keys(updateData).length > 0) {
    const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
    await updateDoc(labelingTaskRef, updateData);
  }
}

/**
 * Add observations to a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} observationIds - The ids of the observations to add to the task
 */
export async function addObservationsToLabelingTask(firestore, clientId, labelingTaskId, observationIds) {
  if (!Array.isArray(observationIds) || observationIds.length === 0) {
    throw new Error('observationIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    observation_ids: arrayUnion(...observationIds),
  });
}

/**
 * Remove observations from a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} observationIds - The ids of the observations to remove from the task
 */
export async function removeObservationsFromLabelingTask(firestore, clientId, labelingTaskId, observationIds) {
  if (!Array.isArray(observationIds) || observationIds.length === 0) {
    throw new Error('observationIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    observation_ids: arrayRemove(...observationIds),
  });
}

/**
 * Add users to a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} userIds - The ids of the users to add to the task
 */
export async function addUsersToLabelingTask(firestore, clientId, labelingTaskId, userIds) {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    throw new Error('userIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    assigned_user_ids: arrayUnion(...userIds),
  });
}

/**
 * Remove users from a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} userIds - The ids of the users to remove from the task
 */
export async function removeUsersFromLabelingTask(firestore, clientId, labelingTaskId, userIds) {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    throw new Error('userIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    assigned_user_ids: arrayRemove(...userIds),
  });
}

/**
 * Get the progress of a labeling task as a fraction (0.0 to 1.0)
 * It returns an object with the progresses. It always contains an "overall" progress.
 * For tasks in individual mode, it returns an object with the progress for each user.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @returns {Object} - The progress of the labeling task
 */
// eslint-disable-next-line no-unused-vars
export async function getLabelingTaskProgress(firestore, clientId, labelingTaskId) {
  // THIS IS A MOCK FUNCTION FOR NOW!
  return {
    overall: 0.78,
  };
}

/**
 * Subscribe to the progress of a labeling task as a fraction (0.0 to 1.0)
 * It returns an object with the progresses. It always contains an "overall" progress.
 * For tasks in individual mode, it returns an object with the progress for each user.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {Function} callback - The callback function to call when the progress changes
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTaskProgress(firestore, clientId, labelingTaskId, callback) {
  // THIS IS A MOCK FUNCTION FOR NOW!
  callback({
    overall: 0.78,
  });
}

/**
 * Add a label of type "single-choice" or "ordinal" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} observationId - The id of the observation
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {string | null} label - The label class. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addSingleChoiceLabelToObservation(
  firestore,
  clientId,
  observationId,
  labelsetId,
  label,
  userId,
  labelingTaskId = 'default',
) {
  const observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), observationId);
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: label, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Add a label of type "multiple-choice" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} observationId - The id of the observation
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {string[] | null} labels - The label classes. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addMultipleChoiceLabelToObservation(
  firestore,
  clientId,
  observationId,
  labelsetId,
  labels,
  userId,
  labelingTaskId = 'default',
) {
  const observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), observationId);
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: labels, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Add a label of type "number" or "single-choice-with-number" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} observationId - The id of the observation
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {number | null} label - The label. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addNumberLabelToObservation(
  firestore,
  clientId,
  observationId,
  labelsetId,
  label,
  userId,
  labelingTaskId = 'default',
) {
  const observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), observationId);
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: label, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Mark observation as labeled by a user for a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} observationId - The id of the observation
 * @param {string} userId - The id of the user
 * @param {string} labelingTaskId - The id of the labeling task
 */
export async function setLabelingCompletedForObservation(firestore, clientId, observationId, userId, labelingTaskId) {
  const observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), observationId);
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
        },
      },
    },
    { merge: true },
  );
}
